export const formatNumber = (num: number | undefined): string => {
  if (num === undefined || num === null) return '0';
  return num.toLocaleString();
};

export const formatCurrency = (num: number | undefined): string => {
  if (num === undefined || num === null) return '$0.00';
  return `$${num.toFixed(2)}`;
};

export const formatPercentage = (num: number | undefined): string => {
  if (num === undefined || num === null) return '0%';
  return `${(num * 100).toFixed(2)}%`;
};

export const prepareCountriesForDisplay = (countries: string | undefined, maxDisplay: number = 5): string => {
  if (!countries) return '';
  
  const countryList = countries.split(',').map(c => c.trim());
  
  if (countryList.length <= maxDisplay) {
    return countryList.join(', ');
  }
  
  const displayed = countryList.slice(0, maxDisplay).join(', ');
  const remaining = countryList.length - maxDisplay;
  return `${displayed} (+${remaining} more)`;
};

export const getSortValue = (app: any, sortBy: string): any => {
  switch (sortBy) {
    case 'Product Name':
      return app.title?.toLowerCase() || '';
    case 'Yesterday Revenue':
      return app.yesterdayRevenue || 0;
    case 'Today Revenue':
      return app.todayRevenue || 0;
    case 'Week Revenue':
      return app.weekRevenue || 0;
    case 'Yesterday Clicks':
      return app.yesterdayBuys || 0;
    case 'Today Clicks':
      return app.todayBuys || 0;
    case 'Week Clicks':
      return app.weekBuys || 0;
    case 'Owner':
      return app.ownerName?.toLowerCase() || '';
    default:
      return app.title?.toLowerCase() || '';
  }
};

export const filterApps = (apps: any[], filters: any): any[] => {
  return apps.filter(app => {
    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const titleMatch = app.title?.toLowerCase().includes(searchLower);
      const idMatch = app.appID?.toLowerCase().includes(searchLower);
      if (!titleMatch && !idMatch) return false;
    }

    // Owner filter
    if (filters.owners.length > 0 && !filters.owners.includes(app.owner)) {
      return false;
    }

    // Platform filter
    if (filters.platforms.length > 0) {
      const appPlatforms = app.platforms?.split(',').map((p: string) => p.trim()) || [];
      const hasMatchingPlatform = filters.platforms.some((platform: string) => 
        appPlatforms.includes(platform)
      );
      if (!hasMatchingPlatform) return false;
    }

    // Has cLinks filter
    if (filters.hasClinks !== null) {
      const hasClinks = app.trackingLinks > 0;
      if (filters.hasClinks !== hasClinks) return false;
    }

    // Has active campaigns filter
    if (filters.hasActiveCampaigns !== null) {
      const hasActive = app.campaigns?.some((c: any) => c.status === 'Active') || false;
      if (filters.hasActiveCampaigns !== hasActive) return false;
    }

    return true;
  });
};

export const getMatchingCampaigns = (app: any): any[] => {
  // Always filter campaigns by campaignMatch flag, like AngularJS does
  if (!app.campaigns || app.campaigns.length === 0) return [];
  
  // Filter campaigns that have campaignMatch === true
  const explicitMatches = app.campaigns.filter((c: any) => c.campaignMatch === true);
  if (explicitMatches.length > 0) return explicitMatches;
  
  // If no campaigns have campaignMatch flag but we have a count, use the first N campaigns
  const matchingCount = app.counts?.appMatchedCampaigns || 0;
  if (matchingCount > 0) {
    // Return the first N campaigns as they come from the backend (preserving backend order)
    return app.campaigns.slice(0, matchingCount);
  }
  
  return [];
};

export const getMatchingClinks = (app: any): any[] => {
  // Always filter clinks by clinkMatch flag, like AngularJS does
  if (!app.clinks || app.clinks.length === 0) return [];
  
  // Filter clinks that have clinkMatch === true
  const explicitMatches = app.clinks.filter((c: any) => c.clinkMatch === true);
  if (explicitMatches.length > 0) return explicitMatches;
  
  // If no clinks have clinkMatch flag but we have a count, use the first N clinks
  const matchingCount = app.counts?.appMatchedClinks || 0;
  if (matchingCount > 0) {
    // Return the first N clinks as they come from the backend (preserving backend order)
    return app.clinks.slice(0, matchingCount);
  }
  
  return [];
};

export const getMatchingCampaignsCount = (app: any): number => {
  // Check if app has explicit count first
  if (app.counts?.appMatchedCampaigns !== undefined) {
    return app.counts.appMatchedCampaigns;
  }
  
  // Otherwise calculate from matching campaigns
  const matching = getMatchingCampaigns(app);
  return matching.length;
};

export const getMatchingClinksCount = (app: any): number => {
  // Check if app has explicit count first  
  if (app.counts?.appMatchedClinks !== undefined) {
    return app.counts.appMatchedClinks;
  }
  
  // Otherwise calculate from matching clinks
  const matching = getMatchingClinks(app);
  return matching.length;
};

export const getMetricValue = (app: any, metric: string, timeframe: 'today' | 'week', showStats: 'overall' | 'matched' = 'overall'): number => {
  const prefix = timeframe === 'today' ? 'today' : 'week';

  switch (metric) {
    case 'clicks':
      // First check for direct properties (like todayClicks, weekClicks)
      const clicksProp = `${prefix}Clicks`;
      if (app[clicksProp] !== undefined) return app[clicksProp] || 0;
      
      // Use the specified stats subset first
      if (showStats === 'overall' && app.stats?.overall) {
        const buysProp = `${prefix}Buys`;
        if (app.stats.overall[buysProp] !== undefined) return app.stats.overall[buysProp] || 0;
      } else if (showStats === 'matched' && app.stats?.matched) {
        const buysProp = `${prefix}Buys`;
        if (app.stats.matched[buysProp] !== undefined) return app.stats.matched[buysProp] || 0;
      }
      
      // Fall back to the other stats subset if the preferred one is not available
      if (showStats === 'matched' && app.stats?.overall) {
        const buysProp = `${prefix}Buys`;
        if (app.stats.overall[buysProp] !== undefined) return app.stats.overall[buysProp] || 0;
      } else if (showStats === 'overall' && app.stats?.matched) {
        const buysProp = `${prefix}Buys`;
        return app.stats.matched[buysProp] || 0;
      }
      return 0;
      
    case 'fillRate':
      // Check direct properties first
      const fillRateProp = `${prefix}RTBFillRate`;
      if (app[fillRateProp] !== undefined) return app[fillRateProp] || 0;
      
      // Use the specified stats subset first
      if (showStats === 'overall' && app.stats?.overall) {
        const statsFillRateProp = `${prefix}RTBFillRate`;
        if (app.stats.overall[statsFillRateProp] !== undefined) return app.stats.overall[statsFillRateProp] || 0;
        const statsFillRateProp2 = `${prefix}FillRate`;
        if (app.stats.overall[statsFillRateProp2] !== undefined) return app.stats.overall[statsFillRateProp2] || 0;
      } else if (showStats === 'matched' && app.stats?.matched) {
        const fillRateProp = `${prefix}FillRate`;
        if (app.stats.matched[fillRateProp] !== undefined) return app.stats.matched[fillRateProp] || 0;
      }
      
      // Fall back to the other stats subset
      if (showStats === 'matched' && app.stats?.overall) {
        const statsFillRateProp = `${prefix}RTBFillRate`;
        if (app.stats.overall[statsFillRateProp] !== undefined) return app.stats.overall[statsFillRateProp] || 0;
        const statsFillRateProp2 = `${prefix}FillRate`;
        if (app.stats.overall[statsFillRateProp2] !== undefined) return app.stats.overall[statsFillRateProp2] || 0;
      } else if (showStats === 'overall' && app.stats?.matched) {
        const fillRateProp = `${prefix}FillRate`;
        return app.stats.matched[fillRateProp] || 0;
      }
      return 0;
      
    case 'conversions':
      // Check direct properties first
      const installsProp = `${prefix}Installs`;
      if (app[installsProp] !== undefined) return app[installsProp] || 0;
      
      // Use the specified stats subset first
      if (showStats === 'overall' && app.stats?.overall) {
        if (app.stats.overall[installsProp] !== undefined) return app.stats.overall[installsProp] || 0;
      } else if (showStats === 'matched' && app.stats?.matched) {
        if (app.stats.matched[installsProp] !== undefined) return app.stats.matched[installsProp] || 0;
      }
      
      // Fall back to the other stats subset
      if (showStats === 'matched' && app.stats?.overall) {
        if (app.stats.overall[installsProp] !== undefined) return app.stats.overall[installsProp] || 0;
      } else if (showStats === 'overall' && app.stats?.matched) {
        return app.stats.matched[installsProp] || 0;
      }
      return 0;
      
    case 'conversionRate':
      const installs = getMetricValue(app, 'conversions', timeframe, showStats);
      const clicks = getMetricValue(app, 'clicks', timeframe, showStats);
      return clicks > 0 ? installs / clicks : 0;
      
    case 'revenue':
      // Check direct properties first
      const revenueProp = `${prefix}Revenue`;
      if (app[revenueProp] !== undefined) return app[revenueProp] || 0;
      
      // Use the specified stats subset first
      if (showStats === 'overall' && app.stats?.overall) {
        if (app.stats.overall[revenueProp] !== undefined) return app.stats.overall[revenueProp] || 0;
      } else if (showStats === 'matched' && app.stats?.matched) {
        if (app.stats.matched[revenueProp] !== undefined) return app.stats.matched[revenueProp] || 0;
      }
      
      // Fall back to the other stats subset
      if (showStats === 'matched' && app.stats?.overall) {
        if (app.stats.overall[revenueProp] !== undefined) return app.stats.overall[revenueProp] || 0;
      } else if (showStats === 'overall' && app.stats?.matched) {
        return app.stats.matched[revenueProp] || 0;
      }
      return 0;
      
    default:
      return 0;
  }
};

export const getCountryCodes = (app: any): string => {
  // First check if the app has a countries property directly
  if (app.countries) {
    if (typeof app.countries === 'string') {
      // If it's a string, split and process
      return app.countries.split(',').map((c: string) => c.trim().toUpperCase()).join(', ');
    } else if (Array.isArray(app.countries)) {
      // If it's an array, join them
      return app.countries.map((c: string) => c.trim().toUpperCase()).join(', ');
    }
  }

  // Otherwise check campaigns for countries
  if (!app.campaigns || app.campaigns.length === 0) return '';

  const countries = new Set<string>();
  app.campaigns.forEach((campaign: any) => {
    if (campaign.countries) {
      // Handle string format
      if (typeof campaign.countries === 'string') {
        const countryList = campaign.countries.split(',').map((c: string) => c.trim());
        countryList.forEach((country: string) => {
          if (country.length === 2) {
            countries.add(country.toUpperCase());
          } else if (country.length > 2) {
            // For longer strings, just take first 2 chars
            countries.add(country.substring(0, 2).toUpperCase());
          }
        });
      }
    }
  });

  return Array.from(countries).sort().join(', ') || '';
};